/**
 * FollowerSystem — Manages shapes that follow a target entity with an offset.
 * Extracted from VFXEffectPlayer.
 *
 * Phase H — typed against substrate-agnostic IShape (instead of
 * Phaser.GameObjects.Shape). Position mutations (.x / .y) stay direct.
 */

import type { IShape } from '../../rendering/IRenderer';
import { entityVfxAnchor } from './entityVfxAnchor';

interface FollowerData {
  targetId: string;
  offsetX: number;
  offsetY: number;
  cachedTarget?: any;
}

export class FollowerSystem {
  private followers = new Map<IShape, FollowerData>();

  /** Register a shape as a follower. */
  public add(
    obj: IShape,
    targetId: string,
    offsetX: number,
    offsetY: number,
  ): void {
    this.followers.set(obj, { targetId, offsetX, offsetY });
  }

  /** Remove a shape from the follower system. */
  public remove(obj: IShape): void {
    this.followers.delete(obj);
  }

  /** Update all active followers. Call from VFXEffectPlayer.update(). */
  public update(resolveTarget: (id: string) => any): void {
    this.followers.forEach((data, obj) => {
      if (!obj.active) {
        this.followers.delete(obj);
        return;
      }

      let target = data.cachedTarget;
      if (!target || target.isDestroyed || !target.position) {
        target = resolveTarget(data.targetId);
        data.cachedTarget = target;
      }

      const anchor = entityVfxAnchor(target);
      if (anchor) {
        obj.x = anchor.x + data.offsetX;
        obj.y = anchor.y + data.offsetY;
      }
    });
  }

  public clear(): void {
    this.followers.clear();
  }
}
